/**
 * The mark at the top of the rail: the shop's own, if the theme bundles one, otherwise a generic
 * shopfront drawn in the shop's colour.
 *
 * There is no logo upload and nothing stored — that was the explicit call, and it is a good one.
 * A logo store means an image pipeline, a moderation question and a 404 to design around, all to
 * solve a problem the theme already solves: a shop is recognisable because its whole app is a
 * different colour, not because of a 26px glyph. So the fallback is the normal case here, and it
 * is drawn rather than fetched — it inherits `currentColor`, which is pointed at
 * `--pos-brand-base`, so Diamond's mark is blue for the same reason Diamond's rail is.
 *
 * Deliberately duplicated from the POS's copy rather than lifted into `@pos/shared`: that package
 * is contracts and transport, it ships no components, and the two differ where their apps do —
 * this one sizes through MUI's `sx`, the other through Tailwind. Two small SVGs is a cheaper
 * price than the first React component in a package that has none.
 */

import Box from '@mui/material/Box';

import { shopThemeFor } from '@pos/tokens';

type Props = {
  /** The shop's theme code. Unknown or missing lands on the default look. */
  readonly themeCode: string | null | undefined;
  readonly size?: number;
  /**
   * Force the generic glyph even where the theme bundles a logo. For a sign-in screen on a
   * device that has never signed in: it must not open on any shop's artwork, OriTech's included,
   * because nobody has said which shop it is yet.
   */
  readonly neutral?: boolean;
};

export function ShopMark({ themeCode, size = 26, neutral = false }: Props) {
  const theme = shopThemeFor(themeCode);

  if (!neutral && theme.logoMark !== null) {
    return <Box component="img" src={theme.logoMark} alt="" sx={{ width: size, height: size }} />;
  }

  const glyph = neutral ? 'storefront' : theme.code === 'diamond' ? 'rhombus' : 'storefront';

  return (
    <Box
      component="svg"
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden="true"
      sx={{ color: 'var(--pos-brand-base)', flexShrink: 0 }}
    >
      {glyph === 'rhombus' ? (
        <>
          {/* Diamond: a four-sided rhombus outline, at the nav icons' stroke weight, in the shop's own
              accent. Simple on purpose — a mark that reads at 26px is a shape, not a picture. */}
          <path
            d="M16 4 28 16 16 28 4 16Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path d="M16 4v24M4 16h24" stroke="currentColor" strokeWidth="1.25" strokeLinejoin="round" opacity="0.45" />
        </>
      ) : (
        <>
      {/* An awning over a doorway, at the same stroke weight as the rail's nav icons so it reads
          as part of the app rather than a placeholder somebody forgot to replace. */}
      <path
        d="M5 12.5h22v13a1.5 1.5 0 0 1-1.5 1.5h-19A1.5 1.5 0 0 1 5 25.5v-13Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M4 8.2A1.2 1.2 0 0 1 5.2 7h21.6A1.2 1.2 0 0 1 28 8.2v2.1a2.2 2.2 0 0 1-4.4 0 2.2 2.2 0 0 1-4.4 0 2.2 2.2 0 0 1-4.4 0 2.2 2.2 0 0 1-4.4 0 2.2 2.2 0 0 1-4.4 0V8.2Z"
        fill="currentColor"
        fillOpacity="0.22"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M13 27v-6.2a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1V27"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
        </>
      )}
    </Box>
  );
}
